import { observable, action, computed, makeObservable } from "mobx";
import { getTruthyValue } from "@draftkings/sportsbook-common-utils";
import { formatLoadBetsTrackingData, Tracker } from "../../helpers";
import { telemetry } from "../../constants";
import { eventDeepMerge, unifyEvent } from "../../helpers";
import { conditionalMap } from "../../helpers";
import { mapInitialResponse, mapNextBetsPageResponse, mapUpdateResponse, proxyMsgPackMapper } from "./mappers";
export class BetsRetriever {
    constructor(options) {
        this.onData = (data) => this.handleData(data);
        this.onError = (error) => this.onMyBetsError(error);
        this.setPushMessageHandler = (id) => {
            if (id) {
                this.pushMessageId = id;
                this.pushMessageHandler.set(id, this.handleEventsUpdates.bind(this));
            }
        };
        this.handleBetsResponse = (response) => {
            const result = "result" in response.data ? response.data.result.initial : response.data;
            const data = conditionalMap(this.isMsgpack, mapInitialResponse, result);
            this.onMyBetsData({
                type: response.type,
                data,
                id: response.id
            });
            this.setPushMessageHandler(response.id);
        };
        this.getCurrentCount = () => {
            const currentCount = Math.min(this.bets.size - this.skip, this.config.maxBetsSizeReconnect);
            return currentCount || this.config.numberOfBets;
        };
        this.onOpen = () => {
            this.loadMoreBets({
                filter: {
                    status: this.config.status,
                },
                pagination: {
                    count: this.config.numberOfBets,
                    skip: this.skip,
                }
            }, "initialBetsPage");
        };
        this.setMyBetsSubscription = (myBetsSubscription) => {
            this.myBetsSubscription = myBetsSubscription;
        };
        this.sendRequest = (message) => {
            if (typeof this.myBetsSubscription !== "undefined") {
                this.myBetsSubscription.send(message);
            }
        };
        this.activate = (myBetsSubscription) => {
            if (!this.isLoggedInOnLoad) {
                return;
            }
            this.setMyBetsSubscription(myBetsSubscription);
            this.onOpen();
        };
        this.deactivate = () => {
            var _a;
            this.pushMessageId = "";
            this.pushMessageHandler.clear();
            this.clearBets();
            (_a = this.myBetsSubscription) === null || _a === void 0 ? void 0 : _a.dispose();
            this.myBetsSubscription = undefined;
        };
        this.resetLiveEvents = (eventId) => {
            const existingEvent = this.events.get(eventId);
            if (existingEvent && existingEvent.gameScore) {
                existingEvent.isLive = false;
                existingEvent.gameScore.isSettlementScore = true;
            }
        };
        this.onMyBetsData = (payload) => {
            this.handleEvents(Object.values(payload.data.events), "set");
            this.updateBets(payload.data.bets);
            if (this.isInitialLoad) {
                this.isInitialLoad = false;
                Tracker.getInstance().trackEvent(telemetry.events.myBets, formatLoadBetsTrackingData({
                    action: telemetry.actions.myBets.openTab,
                    status: "All",
                    previousStatus: "",
                    bets: this.bets,
                    events: this.events,
                }));
            }
            if (this.bets.size >= this.skip) {
                this.setMyBetsListStatus("Loaded");
            }
            else {
                this.setMyBetsListStatus("BetsEnd");
            }
        };
        this.onMyBetsError = (error) => {
            this.setMyBetsListStatus("status" in error.data && error.data.status ? error.data.status : "GeneralError");
        };
        this.config = options.config;
        this.isLoggedInOnLoad = options.isLoggedInOnLoad;
        this.myBetsListStatus = options.isLoggedInOnLoad ? "Initial" : "Unauthorized";
        this.updateHeaderBalance = options.updateHeaderBalance;
        this.bets = observable.map(new Map());
        this.events = observable.map(new Map());
        this.skip = 0;
        this.myBetsSubscription = undefined;
        this.isInitialLoad = true;
        this.lastRequest = {
            filter: {
                status: this.config.status,
            },
            pagination: {
                count: this.config.numberOfBets,
                skip: this.skip,
            },
        };
        this.pushMessageId = "";
        this.pullMessageHandlersMap = {
            initialBetsPage: (payload) => this.handleBetsResponse(payload),
            nextBetsPage: (payload) => this.handleNextBetsPage(payload),
        };
        this.pushMessageHandler = new Map();
        this.isMsgpack = options.isMsgpack;
        makeObservable(this, {
            myBetsListStatus: observable,
            clearBets: action.bound,
            onBetCashedOut: action.bound,
            updateBets: action.bound,
            handleEvents: action.bound,
            setMyBetsListStatus: action.bound,
            resetLiveEvents: action,
            mergeEvents: action,
            status: computed,
        });
    }
    handleNextBetsPage(response) {
        const result = "result" in response.data ? response.data.result : response.data;
        const data = conditionalMap(this.isMsgpack, mapNextBetsPageResponse, result);
        this.onMyBetsData({
            type: response.type,
            data,
            id: response.id
        });
    }
    handleEventsUpdates(response) {
        const updates = conditionalMap(this.isMsgpack, proxyMsgPackMapper(mapUpdateResponse, response.data), response.data.result);
        if ("eventsUpdate" in updates) {
            const eventUpdates = updates.eventsUpdate.data;
            // currnetly we are not able to add new pushed event due to the structure of
            // event on update messages which is missing required fields in the first event
            // and also we dont receive updates for bets so every time we send eventIds which exists
            this.updateEvents(eventUpdates["change"].events);
            this.removeEvent(eventUpdates["remove"].events);
        }
        if ("legTrackingUpdate" in updates) {
            updates.legTrackingUpdate.forEach((e) => {
                const event = this.events.get(e.eventId);
                if (event) {
                    event.marketMap = Object.assign(Object.assign({}, event.marketMap), { [e.marketId]: e.totalPoints });
                }
            });
        }
    }
    removeEvent(eventIds) {
        eventIds.forEach((id) => {
            this.resetLiveEvents(id);
        });
    }
    handleReconnect() {
        this.pushMessageHandler.delete(this.pushMessageId);
        this.pushMessageId = "";
        this.skip = 0;
        let currentCount = this.getCurrentCount();
        this.sendRequest({
            method: "initialBetsPage",
            params: Object.assign(Object.assign({}, this.lastRequest), { pagination: {
                    count: currentCount,
                    skip: 0
                } })
        });
        this.skip = currentCount;
        while (this.bets.size > this.skip) {
            currentCount = this.getCurrentCount();
            this.sendRequest({
                method: "nextBetsPage",
                params: Object.assign(Object.assign({}, this.lastRequest), { pagination: {
                        count: currentCount,
                        skip: this.skip
                    } })
            });
            this.skip += currentCount;
        }
    }
    setStatusOnClose() {
        this.setMyBetsListStatus("Initial");
    }
    handleData(data) {
        const messagaHandler = this.pushMessageHandler.get(getTruthyValue(data.id, "")) || this.pullMessageHandlersMap[data.type];
        messagaHandler === null || messagaHandler === void 0 ? void 0 : messagaHandler(data);
    }
    loadMoreBets(request, messageType) {
        if (typeof this.myBetsSubscription !== "undefined") {
            this.lastRequest = request;
            this.setMyBetsListStatus("Loading");
            this.skip = this.skip + this.config.numberOfBets;
            this.myBetsSubscription.send({
                method: messageType,
                params: request,
            });
        }
    }
    clearBets() {
        this.myBetsListStatus = "Initial";
        this.skip = 0;
        this.bets.clear();
        this.events.clear();
    }
    onBetCashedOut(operation) {
        var _a;
        const betId = operation.cashOutOperation.cashOutInformation.betId;
        const bet = this.bets.get(betId);
        if (typeof bet !== "undefined") {
            const bonus = ((_a = bet.bonus) === null || _a === void 0 ? void 0 : _a.bonusType) === "BetBoost" && bet.bonus.boostType === "GuaranteedPayout" ? Object.assign(Object.assign({}, bet.bonus), { boostStatus: "Forfeit" }) : bet.bonus;
            this.bets.set(betId, Object.assign(Object.assign({}, bet), { settlementStatus: "CashOut", returns: operation.cashOutOperation.cashOutAmount, selections: bet.selections.map(s => (Object.assign(Object.assign({}, s), { settlementStatus: "CashOut" }))), bonus }));
        }
        this.updateHeaderBalance();
    }
    updateBets(bets) {
        bets.forEach(bet => {
            this.bets.set(bet.betId, bet);
        });
    }
    updateEvents(events) {
        events.forEach(e => {
            const existingEvent = this.events.get(e.id);
            if (existingEvent) {
                const unifiedEvent = unifyEvent(e);
                this.mergeEvents(existingEvent, unifiedEvent);
            }
        });
    }
    mergeEvents(existingEvent, newEvent) {
        eventDeepMerge(existingEvent, newEvent);
        if (existingEvent.gameScore && newEvent.gameScore && newEvent.gameScore.combinedSecondTierScores) {
            const combinedSecondTierScores = getTruthyValue(existingEvent.gameScore.combinedSecondTierScores, []);
            newEvent.gameScore.combinedSecondTierScores.forEach((el, i) => combinedSecondTierScores[i] = el);
        }
    }
    handleEvents(events, observableKey) {
        events.forEach(event => {
            this.events[observableKey](event.eventId, event);
        });
    }
    setMyBetsListStatus(status) {
        this.myBetsListStatus = status;
    }
    get status() {
        return this.myBetsListStatus;
    }
}
//# sourceMappingURL=BetsRetriever.js.map